/** Company statuses that may browse, shortlist and complete the intent questionnaire. */
export const EXPLORE_OPPORTUNITIES_STATUSES = ["SYSTEM_VERIFIED", "UNDER_REVIEW", "VERIFIED"] as const;

/** Company statuses that may send an express-interest / collaboration request (admin-verified). */
export const SEND_INTEREST_REQUEST_STATUSES = ["VERIFIED"] as const;

export type ExploreOpportunitiesStatus = (typeof EXPLORE_OPPORTUNITIES_STATUSES)[number];
export type SendInterestRequestStatus = (typeof SEND_INTEREST_REQUEST_STATUSES)[number];

export function canExploreOpportunities(status: string | null | undefined): boolean {
  return Boolean(
    status && (EXPLORE_OPPORTUNITIES_STATUSES as readonly string[]).includes(status),
  );
}

export function canSendInterestRequest(status: string | null | undefined): boolean {
  return Boolean(
    status && (SEND_INTEREST_REQUEST_STATUSES as readonly string[]).includes(status),
  );
}

/** Written on the signup company shell until the client saves a real headquarters. */
export const SIGNUP_PENDING_HEADQUARTERS_MARKER = "__GT_SIGNUP_PENDING__";

/** Statuses that mean the company form has been saved at least once. */
export const PROFILE_CREATED_STATUSES = ["UNDER_REVIEW", "SYSTEM_VERIFIED", "VERIFIED", "ACTION_REQUIRED"] as const;

export const COMPLETE_PROFILE_BEFORE_REACHOUT_MESSAGE =
  "Complete your company profile before reaching out.";

export const ADMIN_VERIFIED_BEFORE_REACHOUT_MESSAGE =
  "Your company profile must be verified by the admin before you can reach out to companies.";

export function isIncompleteSignupHeadquarters(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return true;
  if (trimmed === SIGNUP_PENDING_HEADQUARTERS_MARKER) return true;
  if (trimmed.toLowerCase() === "pending") return true;
  return false;
}

/**
 * True once the client has saved a real company profile.
 * A website on the signup shell does not count. Pending headquarters never counts,
 * even when a description or later status is present.
 */
export function hasCreatedCompanyProfile(
  company: {
    headquartersCountry?: string | null;
    headquartersCity?: string | null;
    description?: string | null;
    status?: string | null;
  } | null | undefined,
): boolean {
  if (!company) return false;
  if (
    isIncompleteSignupHeadquarters(company.headquartersCountry) ||
    isIncompleteSignupHeadquarters(company.headquartersCity)
  ) {
    return false;
  }
  if (company.description?.trim()) return true;
  if (company.status && (PROFILE_CREATED_STATUSES as readonly string[]).includes(company.status)) return true;
  return false;
}
